import React, { useRef, useEffect, useState, useCallback } from 'react';
import useSWR from 'swr';
import { Navigate } from 'react-router-dom';
import styled from '@emotion/styled';
import decode from 'entity-decode';

import { ADMIN_ACCESS_TOKEN_KEY, getFetcher } from '../../../../App';
import { Input, PrimaryButton } from '@components/atoms';
import { EmailValidationStyle, PwValidationStyle } from '@pages/Login/style';
import { ErrorCode, ErrorMessage } from '@variables/error';
import { useResetRecoilState, useSetRecoilState } from 'recoil';
import { dialogContentsState, popupContentsState } from '@components/templates/Dialog';
import { css } from '@emotion/react';
import { CommonValidationStyle } from '@pages/Popup/CreateAccount/styles';
import { PWD_REGEX, REGEX_WRONG_SPECIAL } from '@variables/utils';
import { submitDebounce } from '../../../../utils/lib';

const MyAccountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  & > div {
    margin: 15px 0 !important;
    position: relative;

    input {
      border-color: #e0e0e0;
      padding-top: 10px;
      &[data-invalid='true' i] {
        border-color: #ff4c4c !important;
      }
    }
    input + span {
      display: flex !important;
      border: unset;
      position: absolute;
      width: 100%;
      left: 20px;
    }
    input[data-invalid='true'] + span {
      color: #ff4c4c !important;
    }

    input::-webkit-input-placeholder,
    textarea:focus::-webkit-input-placeholder {
      /* WebKit browsers */
      color: transparent;
    }
    input:-moz-placeholder,
    textarea:focus:-moz-placeholder {
      /* Mozilla Firefox 4 to 18 */
      color: transparent;
    }
    input::-moz-placeholder,
    textarea:focus::-moz-placeholder {
      /* Mozilla Firefox 19+ */
      color: transparent;
    }
    input:-ms-input-placeholder,
    textarea:focus:-ms-input-placeholder {
      /* Internet Explorer 10+ */
      color: transparent;
    }
  }

  &.my-account {
    & [type='text'],
    & [type='password'] {
      & + label {
        display: block;
        top: 4px;
        left: 16px;
        width: 0;
        height: 0;
        border: none;
        white-space: nowrap;
      }
    }
  }
`;
const CommandSection = styled.section`
  display: flex;
  justify-content: center;
`;

export const REGEX_NUMBER = /[0-9]/;
export const REGEX_ONLY_NUMBER = /[^0-9]/g;
export const REGEX_ONLY_NUMBER_AND_VERTICAL_BAR = /[^0-9|]/g;
export const REGEX_IP = /[^0-9.]/g;
export const REGEX_LOWER_TEXT = /[a-z]/;
export const REGEX_UPPER_TEXT = /[A-Z]/;
export const REGEX_SPECIAL_TEXT = /[`~!@#$%^&*|\\\'\";:\/?]/;
export const REGEX_EMAIL_TEXT = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;
export const REGEX_EMAIL_TEXT2 = /^[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/i;

const MyAccount = ({ token }: any) => {
  const setPopupContents = useSetRecoilState(popupContentsState);
  const setDialogContents = useSetRecoilState(dialogContentsState);
  const resetPopupContents = useResetRecoilState(popupContentsState);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [okDisabled, setOkDisabled] = useState(true);
  const [validAll, setValidAll] = useState({
    currentPwd: false,
    email: false,
    pwd: false,
    confirmPwd: false,
  });

  const confirmRef = useRef<any>(null);
  const [isRendered] = useState(true);
  const url = 'api/me';
  const { data, error } = useSWR(isRendered ? [{ url, accessToken: token }] : null);

  const checkPasswordRegex = (password: string) => {
    try {
      const regexResult =
        Number(REGEX_SPECIAL_TEXT.test(password)) +
        Number(REGEX_LOWER_TEXT.test(password)) +
        Number(REGEX_UPPER_TEXT.test(password));
      return regexResult >= 2 && password.length >= 10 && !REGEX_WRONG_SPECIAL.test(password);
    } catch {
      return false;
    }
  };

  const updatePassword = async () => {
    const result = await getFetcher({
      method: 'PATCH',
      accessToken: token,
      body: { currentPassword, password: newPassword, email },
    })('api/me');

    if (result.code === '200') {
      setPopupContents({ type: 'hidden' });
      setDialogContents({
        type: 'success',
        header: `Your password has been changed successfully.`,
        body: <></>,
        footer: <PrimaryButton onClick={() => setDialogContents({ type: 'hidden' })}>OK</PrimaryButton>,
      });
      return;
    }

    if (ErrorCode[result.code]) {
      // Dialog settings
      setDialogContents({
        type: 'error',
        header: `${ErrorCode[result.code]}`,
        body: <>{ErrorMessage[ErrorCode[result.code]]}</>,
        footer: <PrimaryButton onClick={() => setDialogContents({ type: 'hidden' })}>OK</PrimaryButton>,
      });
    }
  };

  /**
   * submit 상태 변경
   */

  const checkSubmit = useCallback(() => {
    const checkEmailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(email);

    let valid = false;
    if (!newPassword && !confirmPassword) {
      valid = !!currentPassword && !!checkEmailRegex && !validAll.currentPwd && !validAll.email;
    } else {
      valid =
        !!currentPassword &&
        checkPasswordRegex(newPassword) &&
        newPassword === confirmPassword &&
        !!checkEmailRegex &&
        !validAll.currentPwd &&
        !validAll.email &&
        !validAll.pwd &&
        !validAll.confirmPwd;
    }

    setOkDisabled(!valid);

    return () => {
      setDialogContents({ type: 'hidden' });
    };
  }, [currentPassword, newPassword, confirmPassword, email, validAll]);

  useEffect(() => {
    if (!data) return;
    setEmail(data.result.email);
  }, [data]);

  /**
   * blur시 submit 변경 처리
   */
  useEffect(() => {
    checkSubmit();
  }, [validAll]);

  if (error) {
    resetPopupContents();
    return <Navigate to={'/'} replace={true} />;
  }

  if (!data) {
    return <b>Loading...</b>;
  }

  if (data) {
    if (data.code === 'UNAUTHORIZED') {
      resetPopupContents();
      return <Navigate to={'/'} replace={true} />;
    }
  }

  return (
    <MyAccountWrapper className={`my-account`}>
      <Input type={'text'} name={'id'} placeholder={'ID'} value={data.result.userId} disabled />
      <Input
        type={'password'}
        name={'currentPassword'}
        placeholder={'Current Password'}
        required
        value={currentPassword}
        styles={PwValidationStyle}
        onChange={(e) => {
          if (e.target.value.length > 50) {
            setCurrentPassword(e.target.value.slice(0, 50));
            return;
          }
          setCurrentPassword(e.target.value);
        }}
        onBlur={(e) => {
          let clone = JSON.parse(JSON.stringify(validAll));
          clone.currentPwd = false;
          setValidAll(clone);

          if (!e.target.value) {
            e.target.dataset['invalid'] = '';
            return;
          }
        }}
      />
      <Input
        type={'password'}
        name={'password'}
        placeholder={'New Password'}
        required
        value={newPassword}
        styles={PwValidationStyle}
        onChange={(e) => {
          e.target.dataset['invalid'] = '';
          if (e.target.value.length > 50) {
            setNewPassword(e.target.value.slice(0, 50));
            return;
          }
          setConfirmPassword('');
          setNewPassword(e.target.value);
        }}
        onBlur={(e) => {
          let clone = JSON.parse(JSON.stringify(validAll));

          if (!e.target.value) {
            e.target.dataset['invalid'] = '';
            clone.pwd = false;
            setValidAll(clone);
            return;
          } else if (data.result.userId === e.target.value) {
            e.target.dataset['invalid'] = 'sameidpwd';
            clone.pwd = true;
            setValidAll(clone);
            return;
          } else if (!checkPasswordRegex(e.target.value)) {
            e.target.dataset['invalid'] = 'regrex';
            clone.pwd = true;
            setValidAll(clone);
            return;
          } else {
            clone.pwd = false;
            setValidAll(clone);
          }
          e.target.dataset['invalid'] = '';
        }}
      />
      <Input
        type={'password'}
        name={'rePassword'}
        placeholder={'Confirm Password'}
        required
        ref={confirmRef}
        value={confirmPassword}
        styles={PwValidationStyle}
        // data-invalid={newPassword !== confirmPassword ? 'not match' : ''}
        onChange={(e) => {
          e.target.dataset['invalid'] = '';
          if (e.target.value.length > 50) {
            setConfirmPassword(e.target.value.slice(0, 50));
            return;
          }
          setConfirmPassword(e.target.value);
          return;
        }}
        onBlur={(e) => {
          let clone = JSON.parse(JSON.stringify(validAll));

          if (!e.target.value) {
            e.target.dataset['invalid'] = '';
            clone.pwd = false;
            setValidAll(clone);
            return;
          } else if (e.target.value !== newPassword) {
            e.target.dataset['invalid'] = 'not match';
            clone.pwd = true;
            setValidAll(clone);
            return;
          }
          setValidAll(clone);
          e.target.dataset['invalid'] = '';
        }}
      />

      <Input
        type={`text`}
        name={`email`}
        placeholder={`Email Address`}
        required
        value={email}
        onChange={(e) => {
          e.target.dataset['invalid'] = '';
          setEmail(e.target.value);
        }}
        onBlur={(e) => {
          let clone = JSON.parse(JSON.stringify(validAll));
          if (!REGEX_EMAIL_TEXT2.test(e.target.value)) {
            e.target.dataset['invalid'] = 'validate-mix';
            clone.email = true;
            setValidAll(clone);
            return;
          }
          clone.email = false;
          setValidAll(clone);
          e.target.dataset['invalid'] = '';
        }}
        styles={css`
          ${CommonValidationStyle}
          ${EmailValidationStyle}
        `}
      />
      <Input
        type={'text'}
        name={'name'}
        placeholder={'Name'}
        value={`${data.result.firstName}_${data.result.lastName}`}
        disabled
      />
      <Input
        type={'text'}
        name={'department'}
        placeholder={'Department'}
        value={decode(data.result.company)}
        disabled
      />
      <CommandSection>
        <PrimaryButton disabled={okDisabled} onClick={submitDebounce(updatePassword)}>
          OK
        </PrimaryButton>
      </CommandSection>
    </MyAccountWrapper>
  );
};

export default MyAccount;
